/**
 * Client-side error reporting.
 *
 * Intentionally standalone (not part of the repository factory): an app may
 * need to report an error *before* the authenticated API client exists — e.g.
 * a crash on the login screen. So this is a bare `fetch` against the public
 * `/error-reports` endpoint, taking only a base URL and the payload.
 */

export interface ErrorReportPayload {
  app: string;
  message: string;
  stack?: string;
  statusCode?: number;
  url: string;
  userAgent: string;
  appVersion?: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

export interface ReportErrorOptions {
  /** API base URL, e.g. `import.meta.env.VITE_API_URL`. */
  baseUrl: string;
  /** Optional bearer token — attached when available so the backend can link
   *  the report to the company/user. Safe to omit. */
  token?: string;
}

/**
 * POST an error report to the backend. Resolves on 2xx, rejects otherwise so
 * the caller (e.g. the ErrorPage "send" button) can show a failed state.
 */
export async function reportError(
  payload: ErrorReportPayload,
  { baseUrl, token }: ReportErrorOptions
): Promise<{ id: string }> {
  const res = await fetch(`${baseUrl.replace(/\/$/, "")}/error-reports`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Error report failed: ${res.status}`);
  }

  const json = (await res.json()) as { data?: { id: string } };
  return { id: json.data?.id ?? "" };
}
